import React from 'react';
import type {
  GestureResponderEvent,
  PressableProps,
  View,
  ViewProps,
} from 'react-native';
import { Pressable, StyleSheet } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import useMergedRef from '@react-hook/merged-ref';

import { theme } from '@garnish/constants';

import { usePressableState } from '../../../hooks';
import { Icon } from '../Icon';
import { webOnlyStyles } from '../../../utils';

export const IconLink = React.forwardRef<View, IconLinkProps>((props, ref) => {
  const {
    testID,
    accessibilityLabel,
    accessibilityHint,
    accessibilityRole,
    palette,
    style,
    onPress,
    disabled,
    width,
    height,
    iconSize,
    color,
    hitSlop,
    ...iconProps
  } = props;
  const localRef = React.useRef(null);
  const mergedRef = useMergedRef(localRef, ref);
  const pressableState = usePressableState(localRef);
  const [isPressed, setPressed] = React.useState(false);
  const onPressIn = React.useCallback(() => {
    setPressed(true);
  }, [setPressed]);
  const onPressOut = React.useCallback(() => {
    setPressed(false);
  }, [setPressed]);
  const isLight = palette !== 'dark';
  const { isHovered, isActive, isFocused } = pressableState;
  const iconColor = getIconColor({
    disabled,
    isLight,
    isPressed,
    isHovered,
    isActive,
    isFocused,
  });
  const dynamicStyle = useStyle(
    () => ({
      width,
      height,
      backgroundColor: getBackgroundColor({
        disabled,
        isLight,
        isHovered,
        isPressed,
      }),
    }),
    [width, height, isLight, isHovered, isPressed],
  );

  return (
    <Pressable
      ref={mergedRef}
      testID={testID ?? 'sg-icon-link'}
      accessibilityRole={accessibilityRole ?? 'link'}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled }}
      style={[
        styles.container,
        dynamicStyle,
        iconLinkWebStyles,
        isFocused ? iconLinkFocusedWebStyles : undefined,
        style,
      ]}
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      disabled={disabled}
      hitSlop={hitSlop}
    >
      <Icon
        color={color ?? iconColor}
        {...(iconSize ? { width: iconSize, height: iconSize } : {})}
        {...iconProps}
      />
    </Pressable>
  );
});

/**
 * Priority Order:
 * 1. disabled
 * 2. pressed
 * 3. active
 * 4. focused
 * 5. hovered
 * 6. default
 */
const getIconColor = ({
  disabled,
  isPressed,
  isActive,
  isFocused,
  isHovered,
  isLight,
}: IconColorSelection): string => {
  let color = 'default';

  if (disabled) color = 'disabled';
  else if (isPressed) color = 'pressed';
  else if (isActive) color = 'active';
  else if (isFocused) color = 'focus';
  else if (isHovered) color = 'hovered';
  const colorMap = isLight ? IconLinkLightColorMap : IconLinkDarkColorMap;

  return colorMap[color];
};

const getBackgroundColor = ({
  disabled,
  isPressed,
  isHovered,
  isLight,
}: IconColorSelection): string => {
  let color = 'transparentBackground';

  if (!disabled && isPressed) color = 'pressedBackground';
  else if (!disabled && isHovered) color = 'hoveredBackground';
  const colorMap = isLight ? IconLinkLightColorMap : IconLinkDarkColorMap;

  return colorMap[color];
};

const IconLinkLightColorMap: Record<string, string> = {
  default: theme.colors.NEUTRAL_2,
  hovered: theme.colors.NEUTRAL_1,
  pressed: theme.colors.NEUTRAL_1,
  active: theme.colors.GREEN_3,
  focus: theme.colors.NEUTRAL_2,
  disabled: theme.colors.OPACITY.GREEN_1.LIGHT,
  transparentBackground: theme.colors.OPACITY.TRANSPARENT,
  hoveredBackground: theme.colors.OPACITY.GREEN_1.ALMOST_TRANSPARENT,
  pressedBackground: theme.colors.OPACITY.GREEN_1.LIGHTEST,
};

const IconLinkDarkColorMap: Record<string, string> = {
  default: theme.colors.KHAKI_3,
  hovered: theme.colors.KHAKI_3,
  pressed: theme.colors.KHAKI_3,
  active: theme.colors.GREEN_4,
  focus: theme.colors.KHAKI_3,
  disabled: theme.colors.OPACITY.KHAKI_3.LIGHT,
  transparentBackground: theme.colors.OPACITY.TRANSPARENT,
  hoveredBackground: theme.colors.OPACITY.KHAKI_3.ALMOST_TRANSPARENT,
  pressedBackground: theme.colors.OPACITY.KHAKI_3.LIGHTEST,
};

type IconLinkProps = {
  readonly onPress?: ((event: GestureResponderEvent) => void) | null;
  readonly palette?: 'dark' | 'light';
  readonly disabled?: boolean;
  readonly style?: ViewProps['style'];
  readonly iconSize?: number;
} & Omit<React.ComponentProps<typeof Icon>, 'style'> &
  Pick<
    PressableProps,
    | 'hitSlop'
    | 'accessibilityLabel'
    | 'accessibilityHint'
    | 'accessibilityRole'
    | 'testID'
  >;

type IconColorSelection = {
  readonly disabled?: boolean;
  readonly isLight?: boolean;
  readonly isPressed?: boolean;
  readonly isHovered?: boolean;
  readonly isActive?: boolean;
  readonly isFocused?: boolean;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderRadius: 1000,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const iconLinkWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: 2,
});
const iconLinkFocusedWebStyles = webOnlyStyles({
  outlineStyle: 'solid',
});
